import { Upload } from '@aws-sdk/lib-storage';
import config from 'config';
import { SECOND } from 'lib/time';
import { S3ClientProxy } from './s3client-proxy';
import { IFileUploader, UploadData } from './types';
import { type EventHandlers, FileUploaderEmitter } from './file-uploader-emitter';
import { generateKey } from './utils';

const PARTS_LIMIT = 4_000;
const PART_MIN_SIZE = 1024 * 1024 * 10;
const QUEUE_SIZE_THRESHOLD = 1024 * 1024 * 100;

function initUpload(key: string, file: File) {
    return new Upload({
        client: new S3ClientProxy({
            region: config.linode.region,
        }),
        params: {
            Bucket: config.linode.bucket,
            Key: key,
            Body: file,
        },

        // AWS Upload lib counts progress on every uploaded file part instead of every second.
        // To prevent large progress spikes, files smaller than 100 MB are divided into four competing upload queues.
        queueSize: file.size > QUEUE_SIZE_THRESHOLD ? 8 : 4,

        // Minimal part size is 10MB
        partSize: Math.max(PART_MIN_SIZE, Math.ceil(file.size / PARTS_LIMIT)),
    });
}

function getFilenameWithBucket(filename: string) {
    return `${config.linode.bucket}/${filename}`;
}

export class AWSFileUploader extends FileUploaderEmitter implements IFileUploader {
    protected uploads: Map<string, Upload>;

    private wakeLock: WakeLockSentinel | null = null;

    private activeUploads = 0;

    constructor(eventHandlers: EventHandlers) {
        super(eventHandlers);
        this.uploads = new Map();
    }

    private async acquireWakeLock() {
        this.activeUploads += 1;

        if (this.activeUploads === 1 && 'wakeLock' in navigator) {
            try {
                this.wakeLock = await navigator.wakeLock.request('screen');
                this.wakeLock.addEventListener('release', () => {
                    this.wakeLock = null;
                });
            } catch {
                // wake lock can be denied (e.g. low battery policy) — uploads continue normally
            }
        }
    }

    private async releaseWakeLock() {
        this.activeUploads = Math.max(0, this.activeUploads - 1);

        if (this.activeUploads === 0 && this.wakeLock) {
            await this.wakeLock.release();
            this.wakeLock = null;
        }
    }

    async uploadFile(params: UploadData) {
        const filename = generateKey(params);
        await this.acquireWakeLock();

        try {
            const upload = initUpload(filename, params.file);
            const startTime = Date.now();

            upload.on('httpUploadProgress', ({ loaded, total }) => {
                const delta = (Date.now() - startTime) / SECOND;
                const avgSpeed = loaded / delta;
                const remainingSize = total - loaded;

                this.onProgress({
                    ...params,
                    filename: getFilenameWithBucket(filename),
                    progress: Math.floor((loaded / total) * 100),
                    secondsLeft: Math.round(remainingSize / avgSpeed),
                });
            });
            this.uploads.set(filename, upload);

            await upload.done();
            this.uploads.delete(filename);

            this.onComplete({
                ...params,
                object: filename,
            });

            return filename;
        } catch (error) {
            this.uploads.delete(filename);
            this.onError({
                ...params,
                message: error instanceof Error ? error.message : String(error),
            });
            throw error;
        } finally {
            await this.releaseWakeLock();
        }
    }

    async cancelUploadFile(filename: string) {
        return this.uploads.get(filename)?.abort();
    }

    async uploadFileWithoutProgress({ file, name }: { file: File; name: string }) {
        const filename = getFilenameWithBucket(name);
        await this.acquireWakeLock();

        try {
            const upload = initUpload(name, file);
            this.uploads.set(filename, upload);

            await upload.done();
            this.uploads.delete(filename);

            return name;
        } catch (error) {
            this.uploads.delete(filename);
            throw error;
        } finally {
            await this.releaseWakeLock();
        }
    }
}
